<template>
  <div class="flex flex-col w-full h-full gap-4 px-3 py-2 bg-card-bg rounded-md shadow">
    <div
      class="flex justify-end items-center w-full"
      v-if="!toggle.createRiskMitigation && !viewMode"
    >
      <v-button
        :text="$t('risk_assessment.create_risk_mitigation')"
        class="py-2"
        type="primary"
        :disabled="viewMode"
        @click="handleCreateRiskMitigation"
      />
    </div>

    <div class="flex max-w-full flex-col" v-if="toggle.createRiskMitigation">
      <create-risk-mitigation
        @close="closeCreateRiskMitigation"
        @submitForm="submitRiskMitigation"
        @fetchUpdatedList="fetchRiskMitigationList"
        @saveAndNext="saveAndNext"
        :formData="riskMitigationDetail"
        :mode="mode"
        :riskAssessmentId="getRiskAssessmentId"
        :dropdownOptions="dropdownOptions"
      />
    </div>

    <div class="flex flex-col max-w-full gap-4">
      <list-header :headers="computedHeaders" :viewOnlyMode="viewMode" />
      <div
        v-if="!listLoading && riskMitigationList.length"
        class="list-group flex flex-col gap-2 transition-all duration-200 overflow-auto scroll-bar"
        style="max-height: 50vh"
      >
        <transition-group
          type="transition"
          name="flip-list"
          class="flex flex-col space-y-2"
        >
          <div v-for="(el) in riskMitigationList" :key="el.id" :data-id="el.id || null">
            <div class="flex w-full h-8 rounded-lg justify-center items-center">

              <div
                class="grid w-full items-center p-1 bg-gray-100 rounded-md border-2"
                :class="{'border-blue-500' : selectedRowId === el.id}"
              >
                <div class="flex gap-4 w-full text-sm">
                  <div class="flex w-3/12 px-2 truncate">
                    <span :title="el.category">{{ el.category }}</span>
                  </div>
                  <div class="flex w-3/12 truncate" v-if="!viewMode" :title="el.sub_category">
                    {{ el.sub_category || "" }}
                  </div>
                  <div class="flex w-3/12 truncate" v-if="!viewMode" :title="el.title">
                    {{ el.title || "" }}
                  </div>
                  <div class="flex w-2/12 truncate" v-if="!viewMode" :title="el.description">
                    {{ el.description || "" }}
                  </div>
                  <div
                    class="truncate flex items-center text-primary w-1/12 gap-5 pl-4"
                    v-if="!viewMode"
                  >
                    <EditButton
                      :disabled="viewMode || isEdit"
                      size="xs"
                      @click="editMitigation(el)"
                    />
                    <DeleteButton
                      size="xs"
                      :disabled="viewMode || isEdit"
                      @click="deleteMitigation(el)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
      <div
        v-else-if="!riskMitigationList.length && !listLoading"
        class="flex justify-center items-start"
      >
        <span> {{ $t('general.no_data_found') }} </span>
      </div>
      <div v-else-if="listLoading" class="flex justify-center items-start">
        <Loader />
      </div>
    </div>

    <div class="flex justify-end pt-4" v-if="!viewMode">
      <v-button
        :text="$t('actions.save_and_next')"
        class="py-2"
        type="primary"
        :disabled="getDisabledSaveAndNext"
        @click="handleSubmit"
      />
    </div>
    <modal-confirm
      :title="$t('risk_assessment.confirm_modal_title')"
      :message="$t('risk_assessment.delete_field_confirmation')"
      ref="confirm-popup"
    />
  </div>
</template>

<script>
import  { cloneDeep } from 'lodash';
import { riskMitigationTableHeaders } from "./utils/constants";
import { fetchRiskMitigationList, deleteRiskMitigation } from "../service";
import Button from "@shared/components/button";
import CreateRiskMitigation from "./create-risk-mitigation";
import ListHeader from "./risk-list-header.vue";

import Loader from "@shared/loader";
import handleError from "@shared/mixins/handleError.js";
const modalConfirm = () => import("@/components/modal-confirm");
import {checkPermission} from "@shared/utils/functions"
import { mapGetters, mapMutations } from "vuex";
import {
  EditButton,
  DeleteButton,
} from "@shared/components/icon-buttons";

export default {
  name: "RiskMitigation",
  components: {
    "v-button": Button,
    "list-header": ListHeader,

    CreateRiskMitigation,
    Loader,
    modalConfirm,
    EditButton,
    DeleteButton,
  },
  mixins: [handleError],
  data() {
    return {
      toggle: {
        createRiskMitigation: false,
      },
      riskMitigationDetail: null,
      mode: "create",
      listLoading: false,
      riskMitigationList: [],
      isEdit: false,
      selectedRowId: "",
    };
  },
  computed: {
    ...mapGetters([
      'getDisabledSaveAndNext'
    ]),
    getAction() {
      return this.$route.params.action;
    },
    getType() {
      return this.$route.params.type;
    },
    getRiskAssessmentId() {
      return this.$route.params?.id;
    },
    viewMode() {
      return this.$route.params.action === "view" || !checkPermission('riskAssessment.edit');
    },
    computedHeaders() {
      return riskMitigationTableHeaders.map((head) => ({
        ...head,
        label: this.$t(`risk_assessment.mitigation_columns.${head.field}`),
      }))
    },
    dropdownOptions() {
      return {
        categories: [...new Set(this.riskMitigationList.map(item => item.category).filter(Boolean))],
        subcategories: [...new Set(this.riskMitigationList.map(item => item.sub_category).filter(Boolean))],
        titles: [...new Set(this.riskMitigationList.map(item => item.title).filter(Boolean))]
      };
    }
  },
  async mounted() {
    await this.fetchRiskMitigationList();

  },
  methods: {
    ...mapMutations([
      'SET_DISABLED_SAVE_AND_NEXT'
    ]),
    checkPermission,
    async fetchRiskMitigationList() {
      try {
        this.listLoading = true;

        const response = await fetchRiskMitigationList(this.getRiskAssessmentId);

        this.riskMitigationList = this.sortRiskMitigationList(response?.data || []);

        this.saveAndNextDisabledState();
        this.listLoading = false;
      } catch (err) {
        this.listLoading = false;
        this.handleError(err);
      }
    },
    handleCreateRiskMitigation() {
      this.mode = 'create';
      this.riskMitigationDetail = {
        category: "",
        sub_category: "",
        title: "",
        description: "",
      }
      this.isEdit = true;
      this.toggle.createRiskMitigation = true;
      this.SET_DISABLED_SAVE_AND_NEXT(true);
    },
    closeCreateRiskMitigation() {
      this.isEdit = false;
      this.toggle.createRiskMitigation = false;
      this.selectedRowId = "";
      this.saveAndNextDisabledState();
    },
    async submitRiskMitigation(form) {
      if (this.mode === 'edit') {
        const index = this.riskMitigationList.findIndex(item => item.id === this.riskMitigationDetail.id);
        if (index > -1) {
          this.riskMitigationList[index] = {
            ...this.riskMitigationList[index],
            category: form.category,
            sub_category: form.sub_category,
            title: form.title,
            description: form.description,
          };
        }
      } else {
        this.riskMitigationList.push({
          id: Date.now(),
          category: form.category,
          sub_category: form.sub_category,
          title: form.title,
          description: form.description,
        });
      }
    },
    editMitigation(el) {
      if (this.viewMode) return;
      this.selectedRowId = el.id;
      this.SET_DISABLED_SAVE_AND_NEXT(true);
      this.mode = "edit";
      this.riskMitigationDetail = {
        category: el.category,
        sub_category: el.sub_category,
        title: el.title,
        description: el.description,
        id: el.id,
      }
      this.isEdit = true;
      this.toggle.createRiskMitigation = true;
    },
    async handleSubmit() {
      this.selectedRowId = "";
      this.saveAndNext();
    },
    async deleteMitigation(formData) {
      if (this.viewMode) return;

      const promise = await this.$refs["confirm-popup"].show({
        message: this.$t('components.risk_mitigation.delete_confirmation'),
      });
      if (promise) {
        try {
          await deleteRiskMitigation({ 
            riskAssessmentId: this.getRiskAssessmentId, 
            mitigationId: formData.id
          })
          
          const index = this.riskMitigationList.findIndex(item => item.id === formData.id);
          if (index > -1) {
            this.riskMitigationList.splice(index, 1);
          }
          
          this.$toast.success(this.$t('components.risk_mitigation.deleted_successfully'));
          await this.fetchRiskMitigationList();
        } catch (err) {
          console.error(err);
          this.$toast.error(
            err?.response?.data?.detail || this.$t("general.default_error")
          );
        }
      }
      this.$refs["confirm-popup"].close();
    },
    saveAndNext() {
      this.$emit("saveAndNext", { from: "riskmitigation" });
    },
    saveAndNextDisabledState() {
      this.SET_DISABLED_SAVE_AND_NEXT(!this.riskMitigationList.length);
    },
    sortRiskMitigationList(list) {
      const _list = cloneDeep(list);
      return _list.sort((a, b) => {
        const aSort = `${a.category || ''}${a.sub_category || ''}${a.title || ''}`.toLowerCase();
        const bSort = `${b.category || ''}${b.sub_category || ''}${b.title || ''}`.toLowerCase();
        return aSort.localeCompare(bSort);
      });
    },

  },
};
</script>